import { ImageDataType } from "@/shared/types";
import type { DirectusFile } from "@directus/sdk";
import type { SafeNavigationItem } from "../typeGuards";
import type { Page, Post } from "../directusTypes";
import { getDirectusHost } from "../directusHost";
import { getRelatedField } from "./getRelatedField";

// Directus image handlers
type ImagePresets = "1200w" | "2400w";

function isSvgAsset(
  image: DirectusFile<import("../directusTypes").Schema>
): boolean {
  if (image.type === "image/svg+xml") return true;
  const filename = image.filename_download;
  return typeof filename === "string" && filename.toLowerCase().endsWith(".svg");
}

const getDirectusImageUrl = (
  imageId: string,
  options?: { preset?: ImagePresets; isSvg?: boolean }
) => {
  if (!imageId) return "";
  if (options?.isSvg) {
    return `${getDirectusHost()}/assets/${imageId}`;
  }
  const preset = options?.preset || "1200w";
  return `${getDirectusHost()}/assets/${imageId}?key=${preset}`;
};

export const getFaviconUrl = (
  favicon?: DirectusFile<import("../directusTypes").Schema> | string | null
): string | null => {
  if (!favicon) return null;
  const assetId = typeof favicon === "object" ? favicon.id : favicon;
  return `${getDirectusHost()}/assets/${assetId}`;
};

export const getAssetDownloadUrl = (assetId: string): string =>
  `${getDirectusHost()}/assets/${assetId}?download`;

export const getImageData = (
  image?: DirectusFile<import("../directusTypes").Schema> | string | null,
  preset?: ImagePresets
): ImageDataType | null => {
  if (!image) return null;

  if (typeof image === "object") {
    const isSvg = isSvgAsset(image);
    const src = getDirectusImageUrl(image.id, { preset, isSvg });
    const alt = image.description || "";
    const title = image.title ?? null;
    return { src, alt, title, isSvg };
  }

  const src = getDirectusImageUrl(image, { preset });
  return { src, alt: "" };
};

// Resolve item link from NavigationItem
export function resolveItemUrl(item: SafeNavigationItem | null) {
  if (!item) {
    return "#";
  }

  if (item.url) {
    return item.url;
  }

  if (item.type === "page") {
    const permalink = getRelatedField<Pick<Page, "permalink">>(
      item.page,
      "permalink"
    );
    if (permalink) {
      return permalink;
    }
  }

  if (item.type === "post") {
    const slug = getRelatedField<Pick<Post, "slug">>(item.post, "slug");
    if (slug) {
      return `/posts/${slug}`;
    }
    if (typeof item.post === "string") {
      return `/posts/${item.post}`;
    }
  }

  return "#";
}
